import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, Plus, Edit, Trash2, Check, X, Download, Calculator,
  Calendar, User, CreditCard, TrendingUp, AlertCircle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useIsMounted } from '@/hooks/useSafeState';

const GestionPaie = () => {
  const { user, token } = useAuth();
  const isMountedRef = useIsMounted();
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustBalanceDialogOpen, setAdjustBalanceDialogOpen] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [autoCalculating, setAutoCalculating] = useState(false);

  const [filters, setFilters] = useState({
    period: '',
    employee_id: 'all',
    status: 'all'
  });

  const [formData, setFormData] = useState({
    employee_id: '',
    period: '',
    days_worked: 0,
    hours_worked: 0,
    overtime_hours: 0,
    base_salary: 0,
    seniority_bonus: 0,
    transport_allowance: 0,
    housing_allowance: 0,
    bonus_1: 0,
    bonus_2: 0,
    other_allowances: 0,
    advances: 0,
    loans: 0,
    other_deductions: 0,
    payment_method: 'virement',
    notes: ''
  });

  const [balanceAdjustment, setBalanceAdjustment] = useState({
    amount: 0,
    description: '',
    transaction_type: 'income'
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const loadData = async () => {
      try {
        const [payrollsRes, employeesRes, bankRes] = await Promise.all([
          axios.get(`${API}/payroll`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          }),
          axios.get(`${API}/users?role=employee`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          }),
          axios.get(`${API}/company-bank-account`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          })
        ]);

        if (isMounted) {
          setPayrolls(payrollsRes.data);
          setEmployees(employeesRes.data);
          setBankAccount(bankRes.data);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && isMounted) {
          toast.error('Erreur lors du chargement');
          console.error(error);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [token]);

  const fetchPayrolls = async (signal = null) => {
    try {
      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.employee_id && filters.employee_id !== 'all') params.append('employee_id', filters.employee_id);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);

      const response = await axios.get(`${API}/payroll?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        ...(signal && { signal })
      });
      if (isMountedRef.current) {
        setPayrolls(response.data);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        if (isMountedRef.current) {
          toast.error('Erreur lors du chargement des paies');
        }
      }
    }
  };

  const fetchBankAccount = async (signal = null) => {
    try {
      const response = await axios.get(`${API}/company-bank-account`, {
        headers: { Authorization: `Bearer ${token}` },
        ...(signal && { signal })
      });
      if (isMountedRef.current) {
        setBankAccount(response.data);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Error fetching bank account:', error);
      }
    }
  };

  const handleAutoCalculate = async () => {
    if (!formData.employee_id || !formData.period) {
      toast.error('Veuillez sélectionner un employé et une période');
      return;
    }

    setAutoCalculating(true);
    const abortController = new AbortController();
    
    try {
      const response = await axios.get(
        `${API}/payroll/calculate-auto/${formData.employee_id}/${formData.period}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );

      setFormData({
        ...formData,
        ...response.data
      });

      toast.success('✅ Calcul automatique effectué basé sur le pointage');
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.message || 'Erreur lors du calcul automatique';
        toast.error(errorMsg);
      }
    } finally {
      setAutoCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const abortController = new AbortController();

    try {
      if (editingPayroll) {
        await axios.put(`${API}/payroll/${editingPayroll.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        });
        toast.success('Paie modifiée avec succès');
      } else {
        await axios.post(`${API}/payroll`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        });
        toast.success('Paie créée avec succès');
      }

      setDialogOpen(false);
      resetForm();
      await fetchPayrolls(abortController.signal);
      await fetchBankAccount(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.response?.data?.detail?.[0]?.msg || error.message || 'Erreur lors de la sauvegarde';
        toast.error(errorMsg);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      period: '',
      days_worked: 0,
      hours_worked: 0,
      overtime_hours: 0,
      base_salary: 0,
      seniority_bonus: 0,
      transport_allowance: 0,
      housing_allowance: 0,
      bonus_1: 0,
      bonus_2: 0,
      other_allowances: 0,
      advances: 0,
      loans: 0,
      other_deductions: 0,
      payment_method: 'virement',
      notes: ''
    });
    setEditingPayroll(null);
  };

  const handleEdit = (payroll) => {
    setEditingPayroll(payroll);
    setFormData({
      employee_id: payroll.employee_id,
      period: payroll.period,
      days_worked: payroll.days_worked,
      hours_worked: payroll.hours_worked,
      overtime_hours: payroll.overtime_hours,
      base_salary: payroll.base_salary,
      seniority_bonus: payroll.seniority_bonus,
      transport_allowance: payroll.transport_allowance,
      housing_allowance: payroll.housing_allowance,
      bonus_1: payroll.bonus_1,
      bonus_2: payroll.bonus_2,
      other_allowances: payroll.other_allowances,
      advances: payroll.advances,
      loans: payroll.loans,
      other_deductions: payroll.other_deductions,
      payment_method: payroll.payment_method,
      notes: payroll.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (payrollId) => {
    if (!window.confirm('Supprimer cette paie ?')) return;
    const abortController = new AbortController();

    try {
      await axios.delete(`${API}/payroll/${payrollId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController.signal
      });
      toast.success('Paie supprimée');
      await fetchPayrolls(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.message || 'Erreur lors de la suppression';
        toast.error(errorMsg);
      }
    }
  };

  const handleValidate = async (payrollId) => {
    const abortController = new AbortController();
    
    try {
      await axios.post(`${API}/payroll/${payrollId}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController.signal
      });
      toast.success('Paie validée');
      await fetchPayrolls(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.message || 'Erreur lors de la validation';
        toast.error(errorMsg);
      }
    }
  };

  const handlePay = async (payrollId) => {
    if (!window.confirm('Marquer cette paie comme payée ? Le montant sera déduit du compte bancaire.')) return;
    const abortController = new AbortController();

    try {
      const response = await axios.post(`${API}/payroll/${payrollId}/pay`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController.signal
      });
      toast.success(`✅ ${response.data.message} - Nouveau solde: ${response.data.new_balance.toFixed(2)} $`);
      await fetchPayrolls(abortController.signal);
      await fetchBankAccount(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.message || 'Erreur lors du paiement';
        toast.error(errorMsg);
      }
    }
  };

  const handleDownloadPayslip = async (payrollId) => {
    const abortController = new AbortController();
    
    try {
      const response = await axios.post(
        `${API}/payroll/generate-payslip?payroll_id=${payrollId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          signal: abortController.signal
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin_paie_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up blob URL
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

      toast.success('Bulletin de paie téléchargé');
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast.error('Erreur lors du téléchargement');
      }
    }
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    const abortController = new AbortController();

    try {
      const adjustmentAmount = balanceAdjustment.transaction_type === 'income'
        ? Math.abs(balanceAdjustment.amount)
        : -Math.abs(balanceAdjustment.amount);

      await axios.put(
        `${API}/company-bank-account/adjust`,
        {
          amount: adjustmentAmount,
          description: balanceAdjustment.description,
          transaction_type: balanceAdjustment.transaction_type
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );

      toast.success('Solde ajusté avec succès');
      setAdjustBalanceDialogOpen(false);
      setBalanceAdjustment({ amount: 0, description: '', transaction_type: 'income' });
      await fetchBankAccount(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        const errorMsg = typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : error.message || 'Erreur lors de l\'ajustement';
        toast.error(errorMsg);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      brouillon: { color: 'bg-gray-500', label: 'Brouillon' },
      valide: { color: 'bg-blue-500', label: 'Validé' },
      paye: { color: 'bg-green-500', label: 'Payé' }
    };

    const config = statusConfig[status] || statusConfig.brouillon;
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header with Bank Account */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-8 w-8 text-green-600" />
              Gestion de la Paie
            </h1>
            <p className="text-gray-600 mt-1">Gérez les salaires et le compte bancaire de l'entreprise</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Paie
          </Button>
        </div>

        {/* Bank Account Card */}
        {bankAccount && (
          <Card className="border-2 border-green-500 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-green-600" />
                  Compte Bancaire Entreprise
                </span>
                {user?.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAdjustBalanceDialogOpen(true)}
                    className="border-green-600 text-green-600"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Ajuster le solde
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Solde Actuel</p>
                  <p className="text-3xl font-bold text-green-600">
                    {bankAccount.balance.toFixed(2)} $
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Devise</p>
                  <p className="text-2xl font-semibold text-blue-600">{bankAccount.currency}</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Dernière MAJ</p>
                  <p className="text-sm font-medium text-gray-700">
                    {format(new Date(bankAccount.last_updated_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Période (AAAA-MM)</Label>
                <Input
                  type="text"
                  placeholder="2024-11"
                  value={filters.period}
                  onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                />
              </div>
              <div>
                <Label>Employé</Label>
                <Select value={filters.employee_id} onValueChange={(value) => setFilters({ ...filters, employee_id: value === 'all' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="valide">Validé</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={fetchPayrolls} className="w-full">
                  Filtrer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payroll List */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des Paies ({payrolls.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {payrolls.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Aucune paie enregistrée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employé</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Période</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Salaire Brut</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Net à Payer</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Statut</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payrolls.map((payroll) => (
                      <tr key={payroll.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{payroll.employee_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {payroll.period}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {payroll.gross_salary.toFixed(2)} $
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                          {payroll.net_salary.toFixed(2)} $
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(payroll.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPayslip(payroll.id)}
                              title="Télécharger le bulletin"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {payroll.status === 'brouillon' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(payroll)}
                                  title="Modifier"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleValidate(payroll.id)}
                                  className="text-blue-600"
                                  title="Valider"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(payroll.id)}
                                  className="text-red-600"
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {payroll.status === 'valide' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePay(payroll.id)}
                                className="text-green-600"
                                title="Marquer comme payé"
                              >
                                <DollarSign className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Payroll Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPayroll ? 'Modifier la Paie' : 'Nouvelle Paie'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee and Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Employé *</Label>
                  <Select
                    value={formData.employee_id}
                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                    required
                    disabled={!!editingPayroll}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Période (AAAA-MM) *</Label>
                  <Input
                    type="text"
                    placeholder="2024-11"
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    required
                    disabled={!!editingPayroll}
                  />
                </div>
              </div>

              {/* Auto Calculate Button */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Calcul Automatique</p>
                      <p className="text-sm text-gray-600">Basé sur les données de pointage</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAutoCalculate}
                    disabled={autoCalculating || !formData.employee_id || !formData.period}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {autoCalculating ? 'Calcul...' : 'Calculer Auto'}
                  </Button>
                </div>
              </div>

              {/* Work Hours */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Heures Travaillées
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Jours travaillés</Label>
                    <Input
                      type="number"
                      value={formData.days_worked}
                      onChange={(e) => setFormData({ ...formData, days_worked: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Heures normales</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.hours_worked}
                      onChange={(e) => setFormData({ ...formData, hours_worked: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Heures supplémentaires</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.overtime_hours}
                      onChange={(e) => setFormData({ ...formData, overtime_hours: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Salary Components */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Éléments de Rémunération
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Salaire de base *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.base_salary}
                      onChange={(e) => setFormData({ ...formData, base_salary: parseFloat(e.target.value) || 0 })}
                      required
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Prime d'ancienneté</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.seniority_bonus}
                      onChange={(e) => setFormData({ ...formData, seniority_bonus: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Indemnité de transport</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.transport_allowance}
                      onChange={(e) => setFormData({ ...formData, transport_allowance: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Indemnité de logement</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.housing_allowance}
                      onChange={(e) => setFormData({ ...formData, housing_allowance: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Prime 1</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.bonus_1}
                      onChange={(e) => setFormData({ ...formData, bonus_1: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Prime 2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.bonus_2}
                      onChange={(e) => setFormData({ ...formData, bonus_2: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Autres indemnités</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.other_allowances}
                      onChange={(e) => setFormData({ ...formData, other_allowances: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Déductions
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Avances</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.advances}
                      onChange={(e) => setFormData({ ...formData, advances: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Prêts</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.loans}
                      onChange={(e) => setFormData({ ...formData, loans: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Autres retenues</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.other_deductions}
                      onChange={(e) => setFormData({ ...formData, other_deductions: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <Label>Mode de paiement</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="especes">Espèces</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes (optionnel)</Label>
                <Input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Annuler
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  {editingPayroll ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Adjust Balance Dialog */}
        <Dialog open={adjustBalanceDialogOpen} onOpenChange={setAdjustBalanceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajuster le Solde du Compte</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdjustBalance} className="space-y-4">
              <div>
                <Label>Type de transaction</Label>
                <Select
                  value={balanceAdjustment.transaction_type}
                  onValueChange={(value) => setBalanceAdjustment({ ...balanceAdjustment, transaction_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Revenu (ajouter)</SelectItem>
                    <SelectItem value="expense">Dépense (retirer)</SelectItem>
                    <SelectItem value="adjustment">Ajustement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Montant *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={balanceAdjustment.amount}
                  onChange={(e) => setBalanceAdjustment({ ...balanceAdjustment, amount: parseFloat(e.target.value) || 0 })}
                  required
                  min="0"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  type="text"
                  value={balanceAdjustment.description}
                  onChange={(e) => setBalanceAdjustment({ ...balanceAdjustment, description: e.target.value })}
                  required
                  placeholder="Raison de l'ajustement..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAdjustBalanceDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Ajuster
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default GestionPaie;
